/**
 * Light, always, until the person chooses dark (docs/frontend/handoff: the
 * prototype opens light and offers Light and Dark, nothing else). The
 * operating system's preference is deliberately not consulted — a dark
 * laptop opening a light product is the design, not a bug.
 *
 * The choice is remembered in this browser so the first paint is right, and
 * saved as the person's `appearance` preference so it follows them (§9);
 * the saved preference wins once it has loaded.
 *
 * Both names are new. Before light was the default, `norum-theme` held
 * whatever the rail's light/system/dark cycle last landed on, and the
 * `theme` preference was a Settings choice that was saved and never applied
 * — so a person who once pressed either would open dark without ever having
 * chosen it. Neither is read; the old key is cleared on the way past.
 */
export type Theme = 'light' | 'dark';

const KEY = 'norum-appearance';
const RETIRED_KEY = 'norum-theme';
/** The experience-plane preference the Appearance control writes. */
export const PREFERENCE = 'appearance';

/** Anything but a stored `dark` — nothing, a stale `system`, garbage — is light. */
export function asTheme(value: unknown): Theme {
  return value === 'dark' ? 'dark' : 'light';
}

export function readTheme(): Theme {
  try {
    localStorage.removeItem(RETIRED_KEY);
    return asTheme(localStorage.getItem(KEY));
  } catch {
    return 'light';
  }
}

export function currentTheme(): Theme {
  return asTheme(document.documentElement.getAttribute('data-theme'));
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  // Form controls and scrollbars follow `color-scheme`, not the tokens.
  root.style.colorScheme = theme;
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // A private window or blocked storage: the theme still applies for
    // this page, it is simply not remembered.
  }
}

const EVENT = 'norum-theme';

export function subscribeTheme(onChange: () => void): () => void {
  window.addEventListener(EVENT, onChange);
  return () => window.removeEventListener(EVENT, onChange);
}

/** Apply, remember, and tell every mounted control. */
export function setTheme(theme: Theme): void {
  applyTheme(theme);
  window.dispatchEvent(new Event(EVENT));
}
